import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { FeatureFlagKey, TicketStatus, formatTicketNumberDisplay } from "@swivl-mono/types";
import React, { useEffect, useRef, useState } from "react";
import { FiArrowRight, FiChevronLeft } from "react-icons/fi";
import { IoMdSend } from "react-icons/io";
import { useNavigate } from "react-router-dom";

import { isFeatureEnabled } from "../../../Helpers/FeatureFlags/isFeatureEnabled";
import ModalModel from "../../../Models/Modal/Modal.Model";
import { getTicketAccessToken, useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";
import { AddLocationV3Modal } from "../../Properties/Modals/AddLocationV3.Modal";
import { ActivityFeed } from "./ActivityFeed";
import { EditableDescription } from "./EditableDescription";
import { EditableTitle } from "./EditableTitle";
import { TicketCustomerFlyout } from "./TicketCustomerFlyout";
import { TicketPanelLayout } from "./TicketPanelLayout";
import { AssigneeField } from "./fields/AssigneeField";
import { DueDateField } from "./fields/DueDateField";
import { GroupField } from "./fields/GroupField";
import { LocationField } from "./fields/LocationField";
import { PriorityField } from "./fields/PriorityField";
import { StatusBadge } from "./fields/StatusBadge";
import { SubscribersField } from "./fields/SubscribersField";
import { TagField } from "./fields/TagField";
import { UserField } from "./fields/UserField";

interface TicketDetailPanelProps {
  /** When set, renders a "Go to Ticket" button in the header (used by the search flyout to open the full page). */
  onExpand?: () => void;
  /** Optional Tasks section rendered between the fields and Activity (the flyout passes an embedded TaskList; the detail page shows tasks in a separate panel). */
  tasksSlot?: React.ReactNode;
}

export function TicketDetailPanel({ onExpand, tasksSlot }: TicketDetailPanelProps = {}) {
  const selectedId = useTicketingStore((s) => s.selectedTicketId);
  const ticket = useTicketingStore((s) => s.ticket);
  const resolved = useTicketingStore((s) => s.resolved);
  const activity = useTicketingStore((s) => s.activity);
  const notes = useTicketingStore((s) => s.notes);
  const loading = useTicketingStore((s) => s.detailLoading);
  const error = useTicketingStore((s) => s.detailError);
  const pending = useTicketingStore((s) => s.pendingByKey);
  const restrictedMode = useTicketingStore((s) => s.restrictedMode);
  const refresh = useTicketingStore((s) => s.refreshSelected);
  const selectTicket = useTicketingStore((s) => s.selectTicket);
  const navigate = useNavigate();

  // Store actions
  const updateStatus = useTicketingStore((s) => s.updateStatus);
  const updatePriority = useTicketingStore((s) => s.updatePriority);
  const updateTitle = useTicketingStore((s) => s.updateTitle);
  const updateDescription = useTicketingStore((s) => s.updateDescription);
  const updateDueDate = useTicketingStore((s) => s.updateDueDate);
  const updateUser = useTicketingStore((s) => s.updateUser);
  const updateLocation = useTicketingStore((s) => s.updateLocation);
  const updateGroup = useTicketingStore((s) => s.updateGroup);
  const updateAssignees = useTicketingStore((s) => s.updateAssignees);
  const updateSubscribers = useTicketingStore((s) => s.updateSubscribers);
  const updateTags = useTicketingStore((s) => s.updateTags);
  const addNote = useTicketingStore((s) => s.addNote);

  const [noteText, setNoteText] = useState("");
  const [footerShadow, setFooterShadow] = useState(false);
  const [customerFlyoutUserId, setCustomerFlyoutUserId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Only trust the server-resolved location name while it still describes the
  // ticket's current location — editing the location updates `locationId`
  // without refetching `resolved`, which would leave a stale name behind.
  const resolvedLocation = resolved?.location?.id === ticket?.locationId ? resolved?.location : undefined;

  useEffect(() => {
    if (selectedId && selectedId > 0) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const updateShadow = (): void => {
      setFooterShadow(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
    };
    updateShadow();
    el.addEventListener("scroll", updateShadow);
    return () => el.removeEventListener("scroll", updateShadow);
  }, [loading, activity, notes]);

  const handleBack = (): void => {
    void selectTicket(null);
    navigate("/inbox", { replace: true });
  };

  const handleAddNote = () => {
    if (noteText.trim()) {
      void addNote(noteText.trim());
      setNoteText("");
    }
  };

  if (!selectedId)
    return (
      <TicketPanelLayout headerLeft={<span className="heading-base-alt text-black">Ticket</span>}>
        <div className="body-sm flex flex-1 items-center justify-center text-grey-600">Select a ticket</div>
      </TicketPanelLayout>
    );
  if (loading)
    return (
      <TicketPanelLayout headerLeft={<span className="heading-base-alt text-black">Ticket</span>}>
        <div className="body-sm flex flex-1 items-center justify-center text-grey-600">Loading...</div>
      </TicketPanelLayout>
    );
  if (error || !ticket)
    return (
      <TicketPanelLayout headerLeft={<span className="heading-base-alt text-black">Ticket</span>}>
        <div className="flex flex-1 items-center justify-center">
          <div className="rounded bg-orange-50 p-2.5">
            <span className="body-sm text-orange-500">{error || "Sorry! This ticket cannot be found."}</span>
          </div>
        </div>
      </TicketPanelLayout>
    );

  const isPending = (key: string) => !!pending[`${key}:${ticket.id}`];
  // Build assignees array with names (we'll need to hydrate this from members in the future)
  const assignees = ticket.assigneeMemberIds.map((memberId) => ({
    memberId,
    memberName: null as string | null // TODO: hydrate from members lookup
  }));

  // Onboarding tickets get a header action that resumes the wizard at the right step.
  // Gone once the ticket is completed (auto-derived or manual) — nothing left to continue.
  const onboardingLink =
    ticket.tags?.includes("onboarding") && ticket.locationId != null && ticket.status !== TicketStatus.COMPLETED ? (
      <button
        type="button"
        className="inline-flex cursor-pointer items-center gap-1 text-sm font-semibold whitespace-nowrap text-orange-500 hover:text-orange-600"
        onClick={() =>
          ModalModel.showModalWithContent(
            <h2>Onboarding: {resolvedLocation?.name ?? "location"}</h2>,
            <AddLocationV3Modal navigate={navigate} resumeLocationId={ticket.locationId ?? undefined} />,
            undefined,
            undefined,
            true,
            true,
            () => void refresh()
          )
        }
      >
        Continue onboarding <FiArrowRight className="size-4" />
      </button>
    ) : null;

  /* Bot-created tickets carry their source conversation (ENG-2929 C4): land on
     THAT conversation in the CC — sibling tickets filed from the same call are
     all reachable through it. Sits left of the status pill in both headers.
     Opens in a new tab (ENG-3015) so the ticket you were reading stays put,
     matching how conversation deep-links behave elsewhere in Studio. */
  const viewConversationButton =
    ticket.botConversationId && ticket.userId ? (
      <a
        href={`/contact-center-v3/conversations/${ticket.userId}?conversation=${ticket.botConversationId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="body-sm-alt flex h-8 cursor-pointer items-center rounded border border-grey-300 bg-white px-3 text-black transition hover:bg-grey-50"
      >
        View Conversation
      </a>
    ) : null;

  const handleViewTranscript = async (): Promise<void> => {
    const token = getTicketAccessToken();
    if (!token) return;
    const res = await API.TicketingAccess.mintTranscriptLink(token);
    window.open(`/conversations/access/${res.token}`, "_blank", "noopener");
  };

  const viewTranscriptButton = ticket.userId ? (
    <button
      type="button"
      onClick={() => void handleViewTranscript()}
      className="body-sm-alt flex h-8 cursor-pointer items-center rounded border border-grey-300 bg-white px-3 text-black transition hover:bg-grey-50"
    >
      View Transcript
    </button>
  ) : null;

  return (
    <>
      <TicketPanelLayout
        headerLeft={
          <div className="flex items-center gap-1">
            {!restrictedMode && (
              <button className="flex cursor-pointer items-center justify-center lg:hidden" onClick={handleBack} aria-label="Back">
                <FiChevronLeft className="size-4 text-black" />
              </button>
            )}
            <span className="heading-base-alt text-black">Ticket</span>
            <span className="heading-mono text-grey-600">#{formatTicketNumberDisplay(ticket.number)}</span>
          </div>
        }
        headerRight={
          // Actions then status, in both layouts. In the flyout the whole row is
          // shifted left so the Panel's floating close-X doesn't overlap it.
          <div className={filterClasses("flex items-center gap-3", onExpand && "mr-7")}>
            {!restrictedMode && onboardingLink}
            {!restrictedMode && viewConversationButton}
            {restrictedMode && viewTranscriptButton}
            {onExpand && (
              <button
                type="button"
                onClick={onExpand}
                className="body-sm-alt flex h-8 cursor-pointer items-center rounded border border-grey-300 bg-white px-3 text-black transition hover:bg-grey-50"
              >
                Go to Ticket
              </button>
            )}
            <StatusBadge status={ticket.status} onChange={(status) => void updateStatus(status)} disabled={isPending("status")} />
          </div>
        }
      >
        {/* Scrollable content */}
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
          <div className="px-4 pt-3">
            {/* Title */}
            <div className="mb-3">
              <EditableTitle
                value={ticket.title}
                onChange={(title) => void updateTitle(title)}
                pending={isPending("title")}
                locked={restrictedMode}
              />
            </div>

            {/* Description */}
            <EditableDescription
              value={ticket.description}
              onChange={(desc) => void updateDescription(desc)}
              pending={isPending("description")}
              locked={restrictedMode}
            />

            {/* Properties */}
            <div className={filterClasses("space-y-2", tasksSlot ? "mb-4" : "mb-8")}>
              <UserField
                userId={ticket.userId}
                userName={resolved?.user?.name ?? null}
                onChange={(id, name) => void updateUser(id, name)}
                pending={isPending("user")}
                onView={!restrictedMode && ticket.userId ? () => setCustomerFlyoutUserId(ticket.userId) : undefined}
                locked={restrictedMode}
              />
              {/* Location — above Assignee because the assignee options are
                  scoped to it (ENG-2961). */}
              <LocationField
                locationId={ticket.locationId}
                locationName={resolvedLocation?.name ?? null}
                locationGroupName={resolvedLocation?.groupName ?? null}
                onChange={(id) => void updateLocation(id)}
                pending={isPending("location")}
                locked={restrictedMode}
              />
              <AssigneeField
                assignees={assignees}
                ticketLocationId={ticket.locationId}
                onChange={(ids) => void updateAssignees(ids)}
                pending={isPending("assignees")}
                locked={restrictedMode}
              />
              <SubscribersField
                subscribedMemberIds={ticket.subscribedMemberIds ?? []}
                onChange={(ids) => void updateSubscribers(ids)}
                pending={isPending("subscribers")}
                locked={restrictedMode}
              />
              {isFeatureEnabled(FeatureFlagKey.TicketingGroups) && (
                <GroupField
                  routingGroupSlug={ticket.routingGroupSlug}
                  resolvedGroupName={resolved?.group?.name}
                  onChange={(slug) => void updateGroup(slug)}
                  pending={isPending("group")}
                  locked={restrictedMode}
                />
              )}
              <DueDateField value={ticket.dueDate ?? null} onChange={(d) => void updateDueDate(d)} pending={isPending("dueDate")} />
              <PriorityField value={ticket.priority} onChange={(p) => void updatePriority(p)} pending={isPending("priority")} />
              <TagField tags={ticket.tags} onChange={(tags) => void updateTags(tags)} pending={isPending("tags")} />
            </div>
          </div>

          {/* Tasks (flyout only) — a full-width section between fields and Activity. */}
          {tasksSlot}

          {/* Activity Feed */}
          <div className="px-4 pb-3">
            <div className="-mx-4 border-t border-grey-200"></div>
            <div className="flex h-12 items-center">
              <h3 className="heading-base-alt text-black">Activity</h3>
            </div>
            <div className="my-1">
              <ActivityFeed activities={activity} notes={notes} />
            </div>
          </div>
        </div>

        {/* Leave a note input */}
        <div className={filterClasses("relative z-10 shrink-0 bg-white p-4", footerShadow && "shadow-[0_-2px_4px_rgba(0,0,0,0.1)]")}>
          <div className="flex items-center gap-2 rounded border border-grey-400 bg-gradient-to-b from-grey-50 via-white to-white px-3 py-2">
            <input
              type="text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
              placeholder="Leave a comment"
              className="body-sm flex-1 bg-transparent text-black caret-orange-500 outline-none placeholder:text-grey-600"
            />
            <button
              onClick={handleAddNote}
              disabled={!noteText.trim()}
              className={filterClasses(
                "flex h-[26px] w-[26px] items-center justify-center rounded transition",
                noteText.trim() ? "text-grey-900 hover:text-orange-500" : "text-grey-500"
              )}
              type="button"
            >
              <IoMdSend />
            </button>
          </div>
        </div>
      </TicketPanelLayout>
      <TicketCustomerFlyout userId={customerFlyoutUserId} isOpen={customerFlyoutUserId != null} onClose={() => setCustomerFlyoutUserId(null)} />
    </>
  );
}
